import { useEffect, useState, useRef } from 'react';

function networkSnapshotEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.effectiveType === b.effectiveType &&
    a.type === b.type &&
    a.saveData === b.saveData &&
    a.downlink === b.downlink &&
    a.rtt === b.rtt
  );
}

/**
 * Custom hook to monitor network connectivity and detect network changes
 */
export const useNetworkMonitor = (onNetworkChange) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [networkType, setNetworkType] = useState(null);
  const previousNetworkRef = useRef(null);
  // Keep latest callback without re-subscribing the effect every render.
  const onNetworkChangeRef = useRef(onNetworkChange);
  onNetworkChangeRef.current = onNetworkChange;

  useEffect(() => {
    const notify = (status, info) => {
      onNetworkChangeRef.current?.(status, info);
    };

    const handleOnline = () => {
      console.log('Network: Online');
      setIsOnline(true);
      notify('online');
    };

    const handleOffline = () => {
      console.log('Network: Offline');
      setIsOnline(false);
      notify('offline');
    };

    // Monitor network type changes (if available)
    // Mobile browsers may use different APIs (connection, mozConnection, webkitConnection)
    const checkNetworkType = () => {
      // Try multiple connection APIs for better mobile support
      let connection = null;
      if ('connection' in navigator) {
        connection = navigator.connection;
      } else if ('mozConnection' in navigator) {
        connection = navigator.mozConnection;
      } else if ('webkitConnection' in navigator) {
        connection = navigator.webkitConnection;
      }

      if (connection) {
        const currentType = {
          effectiveType: connection.effectiveType,
          downlink: connection.downlink,
          rtt: connection.rtt,
          saveData: connection.saveData,
          type: connection.type // Mobile-specific: wifi, cellular, etc.
        };

        // Check if network type changed (more lenient comparison for mobile)
        if (previousNetworkRef.current) {
          const prev = previousNetworkRef.current;
          const changed =
            prev.effectiveType !== currentType.effectiveType ||
            prev.type !== currentType.type ||
            Math.abs((prev.downlink || 0) - (currentType.downlink || 0)) > 0.5 ||
            Math.abs((prev.rtt || 0) - (currentType.rtt || 0)) > 50;

          if (changed) {
            console.log('Network type changed:', previousNetworkRef.current, '->', currentType);
            notify('type-changed', currentType);
          }
        } else if (currentType.effectiveType) {
          console.log('Initial network type:', currentType);
        }

        previousNetworkRef.current = currentType;
        setNetworkType((prev) =>
          networkSnapshotEqual(prev, currentType) ? prev : currentType
        );
      }
    };

    // Initial check
    checkNetworkType();

    // Listen to online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Monitor connection changes (if available)
    // Try all possible connection APIs for mobile compatibility
    let connection = null;
    if ('connection' in navigator) {
      connection = navigator.connection;
    } else if ('mozConnection' in navigator) {
      connection = navigator.mozConnection;
    } else if ('webkitConnection' in navigator) {
      connection = navigator.webkitConnection;
    }

    if (connection && 'addEventListener' in connection) {
      connection.addEventListener('change', checkNetworkType);
      try {
        connection.addEventListener('typechange', checkNetworkType);
      } catch (e) {
        // Event not supported, ignore
      }
    }

    // Periodic check for network type (fallback)
    const intervalId = setInterval(checkNetworkType, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      if (connection && 'removeEventListener' in connection) {
        connection.removeEventListener('change', checkNetworkType);
        try {
          connection.removeEventListener('typechange', checkNetworkType);
        } catch (e) {
          // Event not supported, ignore
        }
      }
      clearInterval(intervalId);
    };
  }, []);

  return { isOnline, networkType };
};

